import Head from 'next/head'
import { Typography, Grid, Container, Box, styled } from '@mui/material'
import { SongStarterHeader } from 'components/SongStarterHeader'
import React, { useState } from 'react'
import { Footer } from 'components/Footer'
import { TopSpacing } from 'components/TopSpacing'
import { useForm } from 'react-hook-form'
import { TextField } from 'components/TextField'
import { Checkbox } from 'components/Checkbox'
import { IconText } from 'components/IconText'
import { IconLink } from 'components/IconLink'
import { AccountBalance, Email, Place } from '@mui/icons-material'
import { SongStarterButton } from 'components/SongStarterButton'
import { dataLayerPush } from 'containers/GoogleTracking/utils'
import { useFunctionRequest } from 'hooks/useFunctionRequest'
import { errorLogger } from 'utils/errorLogger'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'components/useSnackbar'

const IconTextWrapper = styled('div')(() => ({
  marginLeft: 12,
  svg: {
    marginRight: 2,
  },
  p: {
    whiteSpace: 'unset',
  },
}))

export const getStaticProps = async ({ locale }: any) => ({
  props: {
    ...(await serverSideTranslations(locale, ['contact', 'common', 'header'])),
  },
})

type contactFormData = {
  name: string
  emailAddress: string
  message: string
  privacy: boolean
}

const Contact = () => {
  const { t } = useTranslation('contact')
  const { t: tcommon } = useTranslation('common')
  const [statusMessage, setStatusMessage] = useState('')
  const makeRequest = useFunctionRequest()
  const { openSnack, Snackbar } = useSnackbar()
  const pageTitle = t<string>('title_text')
  const formObject = useForm<contactFormData>()
  const { handleSubmit, formState } = formObject

  const submit = async (data: contactFormData) => {
    const body = data
    body.message =
      data.message +
      `
    
    MESSAGE FROM: ${data.emailAddress}`
    dataLayerPush('contact_form', { emailData: data })
    await makeRequest({
      functionName: 'contactEmail',
      type: 'public',
      body: data,
    })
      .then(() => {
        formObject.reset()
        openSnack({ message: t<string>('status_message') })
        setStatusMessage(t('status_message'))
      })
      .catch(error => {
        openSnack({
          message:
            error?.message ||
            'Error sending email, please try again or email to info@songstarter.com',
        })
        errorLogger(error)
      })
  }

  const onSubmit = handleSubmit(submit)

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <SongStarterHeader />
      <Snackbar />
      <Container>
        <Typography
          variant='h2'
          component='h1'
          gutterBottom
          color='textPrimary'
          marginTop='160px'
        >
          {pageTitle}
        </Typography>
        <Box maxWidth='750px'>
          <Typography
            variant='subtitle1'
            component='p'
            color='textSecondary'
            gutterBottom
          >
            {t<string>('subtitle_text')}
          </Typography>
        </Box>

        <br />
        <br />
        <Grid container spacing={8}>
          <Grid item xs={12} md={6}>
            {statusMessage === '' ? (
              <form onSubmit={onSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant='h5' gutterBottom>
                      {t<string>('help_question')}
                    </Typography>
                    <Typography gutterBottom color='textSecondary'>
                      {t<string>('response_time_text')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label='Name'
                      name='name'
                      formObject={formObject}
                      rules={{
                        required: {
                          value: true,
                          message: tcommon<string>('required_field'),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label='Email'
                      name='emailAddress'
                      formObject={formObject}
                      rules={{
                        required: {
                          value: true,
                          message: tcommon<string>('required_field'),
                        },
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: t<string>('contact_email_rules_message'),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Message'
                      name='message'
                      formObject={formObject}
                      rules={{
                        required: {
                          value: true,
                          message: tcommon<string>('required_field'),
                        },
                        minLength: {
                          value: 50,
                          message: t<string>('message_length_message'),
                        },
                      }}
                      fullWidth
                      rows={8}
                      multiline
                    />
                    <br />
                    <Checkbox
                      rules={{ required: true }}
                      name='privacy'
                      formObject={formObject}
                      label='I accept the privacy policy'
                    />
                    {!!formState.errors.privacy && (
                      <Typography variant='body2' color='error'>
                        {t<string>('terms_and_conditions')}
                      </Typography>
                    )}
                    <br /> <br />
                    <Typography variant='body2' color='error'></Typography>
                    <SongStarterButton
                      variant='contained'
                      size='medium'
                      onClick={onSubmit}
                    >
                      {t<string>('send_email_button')}
                    </SongStarterButton>
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Box py={2}>
                <Typography color='textSecondary' gutterBottom>
                  {statusMessage}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant='h5'
                  gutterBottom
                  sx={{ marginLeft: '14px' }}
                >
                  {t<string>('company_information')}
                </Typography>
                <br />
                <IconTextWrapper>
                  <IconText
                    icon={<Place />}
                    value='Retentieweg 24, 7572 PH, Oldenzaal, The Netherlands'
                  />
                </IconTextWrapper>
                <br />
                <IconLink
                  icon={<Email style={{ marginLeft: -1 }} />}
                  label='info@songstarter.com'
                  tooltip={t<string>('open_mail_link')}
                  href='mailto:info@songstarter.com'
                />
                <br />
                <IconTextWrapper>
                  <IconText
                    icon={<AccountBalance />}
                    value='VAT: NL861389803B01'
                  />
                </IconTextWrapper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <TopSpacing />
      </Container>
      <Footer />
    </>
  )
}

export default Contact
